// SCR-STAFF-100 — جستجوی سراسری مراجعه‌کننده (Ctrl+K؛ IA §2.1، US-PAT-010): نتایج با نام، شماره پرونده، سن، بخشی از موبایل،
// آخرین مراجعه و نشان‌ها؛ ↑↓ انتخاب، Enter باز کردن پرونده؛ در نتیجه خالی «ثبت جدید» با پیش‌پر کردن ورودی (FLOW-01).
// AP-PAT-02: ورودی خالی ← مراجعه‌کنندگان اخیر کاربر (US-PAT-011)؛ اسکن QR کارت (ورودی «PC1.») ← باز کردن مستقیم پرونده
// (US-PAT-012؛ کارت دستکاری‌شده PHX-PAT-0202 و ابطال‌شده PHX-PAT-0201).
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type PatientSearchItem } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, toJalali } from '@phoenix/ui';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { PatientBadgeChips, patKeys, useDebounced } from './common';

/** خلاصه یک نتیجه (سطر دوم فهرست). */
export function PatientResultSummary({ item }: { item: PatientSearchItem }) {
  const { t } = useTranslation('pat');
  const parts = [
    t('pat:search.file_number', { number: faDigits(item.fileNumber) }),
    item.previousFileNumber ? t('pat:search.previous_file_number', { number: faDigits(item.previousFileNumber) }) : null,
    item.age !== null && item.age !== undefined ? t('pat:search.age', { age: faDigits(item.age) }) : null,
    item.mobileHint ? t('pat:search.mobile_hint', { digits: faDigits(item.mobileHint) }) : null,
    item.lastVisitAt ? t('pat:search.last_visit', { date: toJalali(item.lastVisitAt) }) : t('pat:search.no_visit'),
  ].filter(Boolean);
  return <span>{parts.join(' · ')}</span>;
}

/** جستجوی سرور با تأخیر ۲۵۰ms. */
export function usePatientSearch(text: string, limit = 20) {
  const { tenantApi, session } = useTenantSession();
  const q = useDebounced(text.trim(), 250);
  return useQuery({
    queryKey: [...patKeys.search(session?.tenantId, q), limit],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/patients/patients/search', { params: { query: { q, limit } } })),
    enabled: q.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
}

export interface PatientSearchPaletteProps {
  open: boolean;
  onClose: () => void;
  onOpenPatient: (id: string) => void;
  /** «ثبت جدید» با ورودی فعلی (FLOW-01). */
  onRegister?: (text: string) => void;
}

/** کد کارت مراجعه‌کننده (QR امضاشده — US-PAT-005). */
export function isCardCode(text: string): boolean {
  return /^PC1\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(text.trim());
}

export function PatientSearchPalette({ open, onClose, onOpenPatient, onRegister }: PatientSearchPaletteProps) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const listId = useId();
  const [text, setText] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const card = isCardCode(text);
  const search = usePatientSearch(card ? '' : text);
  const recent = useQuery({
    queryKey: patKeys.recent(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/patients/patients/recent')),
    enabled: open && text.trim().length === 0,
    staleTime: 0,
  });
  const resolve = useMutation({
    mutationFn: (code: string) => unwrap(tenantApi.POST('/api/v1/patients/patients:resolve-card', { body: { code } })),
    onSuccess: (result) => onOpenPatient(result.patientId),
  });
  const showRecent = text.trim().length === 0;
  const items = showRecent ? (recent.data?.items ?? []) : card ? [] : (search.data?.items ?? []);
  const empty = !card && text.trim().length > 0 && search.isSuccess && !search.isPlaceholderData && items.length === 0;

  const resetResolve = resolve.reset;
  useEffect(() => {
    if (!open) {
      setText('');
      setActive(0);
      resetResolve();
    }
  }, [open, resetResolve]);

  useEffect(() => setActive(0), [search.data]);

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => Math.min(index + 1, Math.max(items.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (card) {
        resolve.mutate(text.trim());
        return;
      }
      const selected = items[active];
      if (selected) {
        onOpenPatient(selected.id);
      } else if (empty && onRegister) {
        onRegister(text);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      data-screen-id="SCR-STAFF-100"
      slotProps={{
        // نام دسترس‌پذیر روی عنصر role=dialog (Paper)، نه ریشه Modal (axe aria-dialog-name).
        paper: { 'aria-label': t('pat:search.title'), sx: { alignSelf: 'flex-start', mt: 8 } },
        // FocusTrap گاهی فوکوس را روی Paper می‌گذارد؛ پس از ورود، فوکوس صریح روی ورودی (Ctrl+K ← تایپ فوری).
        transition: { onEntered: () => inputRef.current?.focus() },
      }}
    >
      <DialogContent sx={{ p: 2 }}>
        <TextField
          autoFocus
          inputRef={inputRef}
          fullWidth
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={onKeyDown}
          label={t('pat:search.label')}
          slotProps={{
            htmlInput: {
              'data-testid': 'patient-search-input',
              role: 'combobox',
              'aria-expanded': items.length > 0,
              'aria-controls': listId,
              'aria-activedescendant': items[active] ? `${listId}-${items[active].id}` : undefined,
              autoComplete: 'off',
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
        <Box sx={{ minHeight: 4, mt: 1 }}>{(search.isFetching || resolve.isPending) && <LinearProgress aria-label={t('common:states.loading')} />}</Box>
        {search.error ? <ErrorState error={search.error} compact /> : null}
        {resolve.error ? <ErrorState error={resolve.error} compact /> : null}
        {card && !resolve.error && (
          <Typography color="text.secondary" data-testid="patient-search-card">
            {t('pat:search.card_hint')}
          </Typography>
        )}
        {showRecent && items.length > 0 && (
          <Typography variant="overline" color="text.secondary" component="div" data-testid="patient-search-recent">
            {t('pat:search.recent')}
          </Typography>
        )}
        <List id={listId} role="listbox" aria-label={t('pat:search.results')} dense data-testid="patient-search-results">
          {items.map((item, index) => (
            <ListItemButton
              key={item.id}
              id={`${listId}-${item.id}`}
              role="option"
              aria-selected={index === active}
              selected={index === active}
              onClick={() => onOpenPatient(item.id)}
              onMouseEnter={() => setActive(index)}
              data-testid="patient-search-result"
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center" component="span">
                    <span>{item.displayName}</span>
                    <PatientBadgeChips badges={item.badges} />
                  </Stack>
                }
                secondary={<PatientResultSummary item={item} />}
              />
            </ListItemButton>
          ))}
        </List>
        {empty && (
          <Stack spacing={1} alignItems="flex-start" data-testid="patient-search-empty">
            <Typography color="text.secondary">{t('pat:search.empty')}</Typography>
            {onRegister && (
              <Button
                variant="contained"
                startIcon={<PersonAddAltOutlinedIcon />}
                onClick={() => onRegister(text)}
                data-testid="patient-search-register"
              >
                {t('pat:search.register_new')}
              </Button>
            )}
          </Stack>
        )}
        <Typography variant="caption" color="text.secondary">
          {t('pat:search.hint')}
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
